import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const lc = (s) => String(s || "").toLocaleLowerCase("tr");
const digits = (s) => String(s || "").replace(/\D/g, "");

// options: { value, label, keywords?, hint?, badge?, badgeCls?, disabled?, disabledReason? }
export function Combobox({ options = [], value, onChange, placeholder = "Seçin", searchPlaceholder = "Ara...", emptyText = "Sonuç yok", allowCustom = false, className, disabled, ...props }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const tid = props["data-testid"];
  const selected = options.find((o) => o.value === value);
  const q = lc(query).trim();
  const qd = digits(query);
  const filtered = q
    ? options.filter((o) => lc(o.label).includes(q) || lc(o.keywords).includes(q) || (qd.length >= 3 && digits(o.keywords).includes(qd)))
    : options;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled} data-testid={tid}
          className={cn("w-full justify-between font-normal", !selected && !value && "text-muted-foreground", className)}>
          <span className="truncate flex items-center gap-2 min-w-0">
            <span className="truncate">{selected ? selected.label : (value && allowCustom ? value : placeholder)}</span>
            {selected?.badge && <span className={cn("shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded border leading-none", selected.badgeCls)} data-testid={tid ? `${tid}-badge` : undefined}>{selected.badge}</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start" data-testid={tid ? `${tid}-content` : undefined}>
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} data-testid={tid ? `${tid}-search` : undefined} />
          <CommandList className="max-h-72">
            <CommandEmpty>
              {allowCustom && query.trim() ? (
                <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent"
                  data-testid={tid ? `${tid}-custom` : undefined}
                  onClick={() => { onChange(query.trim()); setOpen(false); setQuery(""); }}>
                  &ldquo;{query.trim()}&rdquo; olarak kullan
                </button>
              ) : emptyText}
            </CommandEmpty>
            <CommandGroup>
              {filtered.map((o, idx) => (
                <CommandItem key={`${o.value}-${idx}`} value={`${o.value}__${idx}`} disabled={o.disabled} data-testid={tid ? `${tid}-item-${idx}` : undefined}
                  className={cn(o.disabled && "opacity-60")}
                  onSelect={() => { if (o.disabled) return; onChange(o.value); setOpen(false); setQuery(""); }}>
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{o.label}</span>
                    {(o.hint || o.disabledReason) && <span className="block text-[10px] text-muted-foreground truncate">{o.disabledReason || o.hint}</span>}
                  </span>
                  {o.badge && <span className={cn("ml-2 shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded border leading-none", o.badgeCls)}>{o.badge}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
